"use client";

import { useMemo } from "react";
import { Check, Copy } from "lucide-react";
import { Streamdown } from "streamdown";
import { code } from "@streamdown/code";
import { cn, fenceFor } from "../utils";
import { useCopyToClipboard } from "../hooks/use-copy-to-clipboard";
import { wrapCodeClassName } from "../data-display/fenced-code-chrome";
import { Button } from "../inputs/button";

export interface CodeBlockProps {
  /** Raw string value. Auto-detected as JSON when it parses. */
  value: string;
  /**
   * Force a language. Defaults to "json" on successful JSON.parse, otherwise "text".
   * Pass any Shiki-supported language id.
   */
  language?: string;
  /** Render a copy affordance inside the bordered container. Defaults to true. */
  copyable?: boolean;
  /** Maximum height for the scroll container. Defaults to `max-h-[60vh]`. */
  maxHeightClass?: string;
  /**
   * `card` is the default fenced-block chrome: a language header on its own surface. `plain` is the
   * highlighted source alone, for a viewer that already frames the file it shows.
   */
  chrome?: "card" | "plain";
  /** Wrap long lines instead of scrolling horizontally; for prose such as Markdown. */
  wrap?: boolean;
  /** Show line numbers. Defaults to true. */
  lineNumbers?: boolean;
  /** Additional class on the outer wrapper. */
  className?: string;
}

const PLAIN_CHROME_CLASS = [
  "[&_[data-streamdown=code-block]]:!my-0 [&_[data-streamdown=code-block]]:!gap-0 [&_[data-streamdown=code-block]]:!rounded-none [&_[data-streamdown=code-block]]:!border-0 [&_[data-streamdown=code-block]]:!bg-transparent [&_[data-streamdown=code-block]]:!p-0",
  "[&_[data-streamdown=code-block-header]]:hidden [&_[data-streamdown=code-block]>.sticky]:hidden",
  "[&_[data-streamdown=code-block-body]]:!max-h-none [&_[data-streamdown=code-block-body]]:!overflow-visible [&_[data-streamdown=code-block-body]]:!rounded-none [&_[data-streamdown=code-block-body]]:!border-0 [&_[data-streamdown=code-block-body]]:!bg-transparent",
].join(" ");

const streamdownPlugins = { code };

/**
 * Render a string of code with Shiki syntax highlighting via `streamdown`.
 * Auto-detects JSON and pretty-prints it. Falls back to plain text on parse failure.
 *
 * Light/dark theme is handled by Streamdown via Shiki's dual-theme support; both
 * are emitted and CSS toggles between them on the `.dark` ancestor.
 *
 * The copy button is rendered INSIDE the bordered container (top-right,
 * always visible, subtle hover affordance) so it reads as part of the
 * snippet rather than floating in the right gutter — matches the
 * convention used by enterprise dev portals (Stripe, GitHub).
 */
export function CodeBlock({
  value,
  language,
  copyable = true,
  maxHeightClass = "max-h-[60vh]",
  chrome = "card",
  wrap = false,
  lineNumbers = true,
  className,
}: CodeBlockProps) {
  const { source, lang, displayValue } = useMemo(() => {
    if (!value) {
      return { source: "", lang: language ?? "text", displayValue: "" };
    }
    const trimmed = value.trim();
    const looksJson = trimmed.startsWith("{") || trimmed.startsWith("[");
    if (!language || language === "json") {
      if (looksJson) {
        try {
          const parsed = JSON.parse(trimmed);
          const pretty = JSON.stringify(parsed, null, 2);
          const fence = fenceFor(pretty);
          return {
            source: fence + "json\n" + pretty + "\n" + fence,
            lang: "json",
            displayValue: pretty,
          };
        } catch {
          // fall through to text
        }
      }
    }
    const finalLang = language ?? "text";
    const fence = fenceFor(value);
    return {
      source: fence + finalLang + "\n" + value + "\n" + fence,
      lang: finalLang,
      displayValue: value,
    };
  }, [value, language]);

  return (
    <div
      className={cn(
        "group relative overflow-hidden rounded-md border border-border/60 bg-muted/20 text-xs leading-relaxed",
        className,
      )}
      data-language={lang}
    >
      <div
        className={cn(
          "overflow-auto",
          // Reserve gutter so the floating copy button never sits on top
          // of code text. Pre's intrinsic padding still applies; we just
          // add right-side breathing room.
          copyable ? "[&_pre]:!pr-12" : "",
          "[&_pre]:!my-0 [&_pre]:!rounded-none [&_pre]:!border-0 [&_pre]:!bg-transparent [&_pre]:!p-4",
          "[&_code]:font-mono",
          chrome === "plain" && PLAIN_CHROME_CLASS,
          wrapCodeClassName(wrap),
          maxHeightClass,
        )}
      >
        <Streamdown
          mode="static"
          controls={false}
          lineNumbers={lineNumbers}
          plugins={streamdownPlugins}
          className="size-full"
        >
          {source}
        </Streamdown>
      </div>
      {copyable ? <CodeBlockCopyButton value={displayValue} /> : null}
    </div>
  );
}

interface CodeBlockCopyButtonProps {
  value: string;
}

function CodeBlockCopyButton({ value }: CodeBlockCopyButtonProps) {
  const { copied, copy } = useCopyToClipboard(value);

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={copy}
      aria-label={copied ? "Copied" : "Copy code"}
      className={cn(
        "absolute right-2 top-2 size-7 border border-transparent bg-background/60 text-muted-foreground backdrop-blur-sm",
        "hover:border-border hover:bg-background hover:text-foreground",
        "focus-visible:border-border focus-visible:bg-background focus-visible:text-foreground",
        "focus-visible:ring-1 focus-visible:ring-ring",
        copied && "border-border bg-background text-foreground",
      )}
    >
      {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
    </Button>
  );
}
